'use client';

import { Contract } from '@ethersproject/contracts';
import { useMemo } from 'react';
import {
  mainNetworkChainId,
  shiberseContractAddresses,
  puppyNetChainId,
  shibariumChainId,
  sepoliaChainId,
} from '../constants';
import { useAccount } from 'wagmi';
import { JsonRpcProvider, Web3Provider } from '@ethersproject/providers';
import SHIBASWAP_ERC20 from '../constants/abis/shibaswap_erc20.json';
import SHIBERSE_LANDSALE_ABI from '../constants/abis/shiberse_landsale.json';
import SHIBERSE_LANDSALE_ETHEREUM_ABI from '../constants/abis/shiberse_ethereum_landsale.json';
import { ChainId } from '@shibaswap/sdk';
// Import the JSON as a default export (Next.js warns about named exports from default-exporting JSON modules)
import MerkleDistributorJson from '@uniswap/merkle-distributor/build/MerkleDistributor.json';

const MERKLE_DISTRIBUTOR_ABI = (MerkleDistributorJson as { abi: any }).abi;

// Public RPC URL used for reads and gas estimation on a given chain
function getRpcUrl(chainId: number): string {
  switch (chainId) {
    case shibariumChainId:
      return 'https://rpc.shibarium.shib.io';
    case puppyNetChainId:
      return 'https://rpc.puppynet.shib.io';
    case sepoliaChainId:
      return 'https://ethereum-sepolia-rpc.publicnode.com';
    case mainNetworkChainId:
    default:
      return 'https://ethereum-rpc.publicnode.com';
  }
}

// Helper to connect a signer to a contract for write operations
// Reads stay on the public RPC provider; transactions use the MetaMask signer
export function connectSigner(contract: Contract | null): Contract | null {
  if (!contract) return null;
  if (typeof window === 'undefined' || !window.ethereum) return contract;

  try {
    const web3Provider = new Web3Provider(window.ethereum);
    const signer = web3Provider.getSigner();

    // Gas estimation is done beforehand on the public RPC provider (see callContractWithRpcGasEstimate)
    return contract.connect(signer);
  } catch (error) {
    console.error('Failed to connect signer:', error);
    return contract;
  }
}

// Helper to safely call a contract method with gas estimation from the public RPC
// This avoids MetaMask rate limiting by estimating gas on the public RPC first
export async function callContractWithRpcGasEstimate(
  rpcContract: Contract | null,
  methodName: string,
  args: any[],
  overrides: any = {}
): Promise<any> {
  if (!rpcContract) throw new Error('Contract is null');

  try {
    // Estimate gas using the public RPC provider (avoids MetaMask rate limiting)
    const gasLimit = await rpcContract.estimateGas[methodName](...args, overrides);

    // Connect signer for the actual transaction
    const signedContract = connectSigner(rpcContract);
    if (!signedContract) throw new Error('Failed to connect signer');

    // Send transaction with pre-estimated gas limit
    return await signedContract[methodName](...args, {
      ...overrides,
      gasLimit: gasLimit.mul(120).div(100), // Add 20% buffer
    });
  } catch (error) {
    throw error;
  }
}

// returns null on errors
// ALWAYS uses a public JsonRpcProvider for ALL read operations
// This avoids MetaMask rate limiting by not routing reads through the wallet
// Note: For write operations (transactions), use connectSigner() helper
export function useContract(
  contractAddress: string | undefined,
  ABI: any,
  _withSignerIfPossible = true
): Contract | null {
  const { chain } = useAccount();

  return useMemo(() => {
    if (!contractAddress || !ABI) return null;

    try {
      // Always use the public RPC provider for the connected chain
      const chainId = chain?.id || mainNetworkChainId;
      const rpcUrl = getRpcUrl(chainId);
      const provider = new JsonRpcProvider(rpcUrl);

      // Create read-only contract with the public RPC provider
      // For write operations, use connectSigner(contract) to add signer
      return new Contract(contractAddress, ABI, provider);
    } catch (error) {
      console.error('Failed to get contract', error);
      return null;
    }
  }, [contractAddress, ABI, chain]);
}

export function useShiberseLandSaleMainnetContract(
  withSignerIfPossible?: boolean
): Contract | null {
  const contractAddress = shiberseContractAddresses[mainNetworkChainId]['LAND_SALE'];
  const contractAbi = SHIBERSE_LANDSALE_ETHEREUM_ABI;

  return useContract(contractAddress, contractAbi, withSignerIfPossible);
}

export function useShiberseLandSaleSeploliaContract(
  withSignerIfPossible?: boolean
): Contract | null {
  const contractAddress = shiberseContractAddresses[sepoliaChainId]['LAND_SALE'];
  const contractAbi = SHIBERSE_LANDSALE_ETHEREUM_ABI;

  return useContract(contractAddress, contractAbi, withSignerIfPossible);
}

export function useShiberseLandSalePuppynetContract(
  withSignerIfPossible?: boolean
): Contract | null {
  const contractAddress = shiberseContractAddresses[puppyNetChainId]['LAND_SALE'];
  const contractAbi = SHIBERSE_LANDSALE_ABI;

  return useContract(contractAddress, contractAbi, withSignerIfPossible);
}

export function useShiberseLandSaleShibariumContract(
  withSignerIfPossible?: boolean
): Contract | null {
  const contractAddress = shiberseContractAddresses[shibariumChainId]['LAND_SALE'];
  const contractAbi = SHIBERSE_LANDSALE_ABI;

  return useContract(contractAddress, contractAbi, withSignerIfPossible);
}

export function useShibaSwapTokenContract(withSignerIfPossible?: boolean): Contract | null {
  const { chain } = useAccount();
  const chainId = chain?.id as keyof typeof shiberseContractAddresses;
  let tokenAddress: string | undefined;

  if (chainId == mainNetworkChainId) {
    tokenAddress = shiberseContractAddresses[mainNetworkChainId]['SHIB_TOKEN'];
  } else if (chainId == puppyNetChainId) {
    tokenAddress = shiberseContractAddresses[puppyNetChainId]['SHIB_TOKEN'];
  } else if (chainId == sepoliaChainId) {
    tokenAddress = shiberseContractAddresses[sepoliaChainId]['SHIB_TOKEN'];
  } else if (chainId == shibariumChainId) {
    tokenAddress = shiberseContractAddresses[shibariumChainId]['SHIB_TOKEN'];
  } else {
    tokenAddress = shiberseContractAddresses[puppyNetChainId]['SHIB_TOKEN'];
  }
  return useContract(tokenAddress, SHIBASWAP_ERC20, withSignerIfPossible);
}

export function useShibaSwapWethTokenContract(withSignerIfPossible?: boolean): Contract | null {
  const { chain } = useAccount();
  const chainId = chain?.id as keyof typeof shiberseContractAddresses;
  let tokenAddress: string | undefined;

  if (chainId == puppyNetChainId) {
    tokenAddress = shiberseContractAddresses[puppyNetChainId]['WETH_TOKEN'];
  } else if (chainId == shibariumChainId) {
    tokenAddress = shiberseContractAddresses[shibariumChainId]['WETH_TOKEN'];
  }
  return useContract(tokenAddress, SHIBASWAP_ERC20, withSignerIfPossible);
}

export function useShibaMerkleDistributorContract(distributorAaddress: {
  [chainId in ChainId]?: string;
}): Contract | null {
  return useContract(
    distributorAaddress[mainNetworkChainId as keyof typeof distributorAaddress],
    MERKLE_DISTRIBUTOR_ABI,
    true
  );
}
